// ─── Notifications: the kind registry ────────────────────────────
//
// A notification is its own entity — the core knows nothing about requests,
// releases or tickets. What a notification IS lives here: a `kind` key, the
// category it groups under, the copy for the settings screen, and how a user is
// allowed to subscribe to it.
//
// That indirection is the point. Adding the next kind — and many more are
// coming, some tied to a record and some not — is one entry in this array plus
// one `notify()` call at the place the thing happens. No schema change, no
// settings-screen change, no migration: preferences store only what a user
// actively changed, so an unset kind falls through to `default` here and is live
// for everyone the moment it deploys. (f-01806)
import { PCP_ENTITY_ROUTES } from './pcp-links.js';
// Kinds ABOUT a record offer the full ladder; kinds with no record (a failed
// sync, a review reminder) have nothing to follow or own, so they are on/off.
const ALL_MODES = ['off', 'follow', 'mine', 'all'];
const ON_OFF = ['off', 'all'];
/** A backlog item's Description or Acceptance Criteria changed (f-02061). Exported
 *  so a list of backlog kinds elsewhere can name it without spelling it out. */
export const BACKLOG_CONTENT_KIND = 'backlog.content';
export const NOTIFICATION_KINDS = [
    // ── Requests ── low volume, org-wide interest: default to everything, which is
    //    what the bulb has always done.
    {
        key: 'request.feature.created', category: 'requests', entityType: 'request',
        label: 'New feature request', description: 'Someone files a feature request.',
        modes: ALL_MODES, default: 'all', phase: 1,
    },
    {
        key: 'request.bug.created', category: 'requests', entityType: 'request',
        label: 'New bug request', description: 'Someone files a bug request.',
        modes: ALL_MODES, default: 'all', phase: 1,
    },
    {
        key: 'request.status', category: 'requests', entityType: 'request',
        label: 'Request status changed', description: 'Planning, planned, in progress, rejected.',
        modes: ALL_MODES, default: 'all', phase: 1,
    },
    {
        key: 'request.implemented', category: 'requests', entityType: 'request',
        label: 'Request implemented', description: 'A request ships.',
        modes: ALL_MODES, default: 'all', phase: 1,
    },
    // ── Releases ── nobody's release is more "theirs" than anyone else's, so on/off.
    {
        key: 'release.published', category: 'releases', entityType: 'release',
        label: 'Release note published', description: "A new version's notes go out.",
        modes: ON_OFF, default: 'all', phase: 1,
    },
    // ── System ── no record behind them; both keep the OFF default the push toggles
    //    they replace already had.
    {
        key: 'sync.error', category: 'system',
        label: 'Sync errors', description: 'An integration sync fails.',
        modes: ON_OFF, default: 'off', permission: 'notifications.errors', phase: 1,
    },
    {
        key: 'review.pending', category: 'system',
        label: 'Items waiting for review', description: 'A count of what is still unreviewed.',
        modes: ON_OFF, default: 'off', phase: 1,
    },
    // ── Synced domains (phase 2) ── one sync pass can touch hundreds of rows, so
    //    `mine` is the default and `all` is a deliberate opt-in. Following an
    //    individual record bypasses this entirely.
    {
        key: 'backlog.status', category: 'backlog', entityType: 'backlog',
        label: 'Backlog item state changed', description: 'A work item moves to another state or board lane.',
        modes: ALL_MODES, default: 'mine', phase: 2,
    },
    {
        key: 'backlog.assigned', category: 'backlog', entityType: 'backlog',
        label: 'Backlog item assigned', description: 'A work item changes hands.',
        modes: ALL_MODES, default: 'mine', phase: 2,
    },
    {
        key: 'backlog.commented', category: 'backlog', entityType: 'backlog',
        label: 'Backlog item commented', description: 'Someone comments on a work item.',
        modes: ALL_MODES, default: 'mine', phase: 2,
    },
    {
        // Its words or its inline images — formatting alone (bold, a highlight, a
        // list, a link) is not news, nor is the HTML being re-saved. (f-02061)
        key: BACKLOG_CONTENT_KIND, category: 'backlog', entityType: 'backlog',
        label: 'Backlog item description changed',
        description: 'The description or acceptance criteria of a work item changes — its text or images, not formatting alone.',
        modes: ALL_MODES, default: 'mine', phase: 2,
    },
    {
        key: 'ticket.replied', category: 'tickets', entityType: 'ticket',
        label: 'Ticket reply', description: 'A customer or agent replies on a Zoho ticket.',
        modes: ALL_MODES, default: 'mine', phase: 2,
    },
    {
        key: 'ticket.status', category: 'tickets', entityType: 'ticket',
        label: 'Ticket status changed', description: 'A ticket moves to another status, or is closed or reopened.',
        modes: ALL_MODES, default: 'mine', phase: 2,
    },
    {
        key: 'topic.replied', category: 'topics', entityType: 'topic',
        label: 'Topic reply', description: 'Someone replies to a community topic.',
        modes: ALL_MODES, default: 'mine', phase: 2,
    },
    {
        key: 'topic.solved', category: 'topics', entityType: 'topic',
        label: 'Topic marked solved', description: 'A topic gets an accepted answer.',
        modes: ALL_MODES, default: 'mine', phase: 2,
    },
    // ── Feedback boards ── local, low volume and inherently collaborative: a board
    //    you are on is a board you want to hear about, so these default to `mine`
    //    like the other record-shaped kinds. A comment's STATE changes are
    //    deliberately not a kind — ticking threads off a to-do rail would be noise.
    {
        key: 'feedback.commented', category: 'feedback', entityType: 'feedback',
        label: 'Feedback comment', description: 'Someone comments or replies on a feedback board.',
        modes: ALL_MODES, default: 'mine', phase: 2,
    },
    {
        key: 'feedback.status', category: 'feedback', entityType: 'feedback',
        label: 'Feedback state changed', description: 'A feedback board moves state.',
        modes: ALL_MODES, default: 'mine', phase: 2,
    },
];
const BY_KEY = new Map(NOTIFICATION_KINDS.map((k) => [k.key, k]));
export function kindDef(key) {
    return BY_KEY.get(key);
}
/** Human label for a category — the settings screen's group headings. */
export const NOTIFICATION_CATEGORY_LABELS = {
    requests: 'Requests',
    releases: 'Releases',
    backlog: 'Backlog',
    tickets: 'Tickets',
    topics: 'Topics',
    feedback: 'Feedback',
    system: 'System',
};
/**
 * What a user's mode for `key` actually is.
 *
 * Deliberately conservative in both directions: a kind with no definition is
 * `off` (never notify on something nobody declared), and a stored value the kind
 * does not offer — a `mine` left behind on a kind that later dropped to on/off —
 * falls back to that kind's default rather than being honoured as garbage.
 */
export function resolveKindMode(key, prefs) {
    const def = BY_KEY.get(key);
    if (!def)
        return 'off';
    const stored = prefs?.[key];
    if (stored && def.modes.includes(stored))
        return stored;
    return def.default;
}
// Each entry names the shared route table rather than spelling the path out
// again, so a moved route is moved in ONE place (pcp-links.ts) and the
// notification feed, the MCP server's `pcpUrl` and the app agree. (f-01975)
const ROUTE_BY_ENTITY = {
    request: PCP_ENTITY_ROUTES.request,
    release: PCP_ENTITY_ROUTES.release,
    backlog: PCP_ENTITY_ROUTES.backlog,
    ticket: PCP_ENTITY_ROUTES.ticket,
    topic: PCP_ENTITY_ROUTES.topic,
    feedback: PCP_ENTITY_ROUTES.feedback,
};
/**
 * Where clicking a notification goes.
 *
 * The stored `entityType`/`entityId` pair is the primary source, NOT the stored
 * url: routes have moved in this codebase before (/discourse/:id → /topics/:id,
 * which survives only because a redirect was left behind), and a url frozen at
 * write time is a liability in a feed that is a month deep. The url is the
 * fallback for kinds with no record (a failed sync → /diagnostics), and the
 * notifications page itself is the last resort so a row is never a dead click.
 */
export function notificationTarget(n) {
    const type = n.entityType;
    if (type && n.entityId && type in ROUTE_BY_ENTITY) {
        return `${ROUTE_BY_ENTITY[type]}${n.entityId}`;
    }
    return n.url || '/notifications';
}
/**
 * Which record a notification belongs to.
 *
 * BOTH halves of the pair, always. Zoho ticket ids and Azure DevOps work-item
 * ids are both bare integers, so a key built from the id alone silently welds
 * ticket 21095 to work item 21095 — and the deck would then mark unrelated rows
 * read together.
 *
 * A row with no record (a failed sync, a review reminder) falls back to its own
 * id, so two of them never merge: they share a null, not a subject.
 */
export function notificationGroupKey(n) {
    if (n.entityType && n.entityId)
        return `${n.entityType}:${n.entityId}`;
    return `solo:${n.id}`;
}
/**
 * Collapse rows about the same record into one group, preserving order.
 *
 * `items` must be newest-first (which is the only order the feed returns), and
 * the groups come back ordered by their newest member.
 *
 * `limit` caps the number of GROUPS, not rows. That distinction is the whole
 * feature: capping rows is what the tray already did. Members are collected
 * before the cap is applied, so a group that survives it still carries every one
 * of its rows and marking it read clears all of them.
 */
export function groupNotifications(items, limit) {
    const byKey = new Map();
    const groups = [];
    for (const item of items) {
        const key = notificationGroupKey(item);
        const existing = byKey.get(key);
        if (existing) {
            existing.push(item);
            continue;
        }
        const rows = [item];
        byKey.set(key, rows);
        // `rows` is pushed by reference and grows as later members are found, so
        // `count` has to be read at the end rather than frozen here.
        groups.push({ key, items: rows, newest: item, count: 0 });
    }
    for (const g of groups)
        g.count = g.items.length;
    return typeof limit === 'number' ? groups.slice(0, limit) : groups;
}
/** The kinds a user may see, given the permissions they hold and what has shipped. */
export function visibleKinds(has, maxPhase = 2) {
    return NOTIFICATION_KINDS.filter((k) => k.phase <= maxPhase && (!k.permission || has(k.permission)));
}
/**
 * Backlog: also hear about the DIRECT children (User Stories and Bugs) of a
 * Feature that is yours — one you follow (at Follow and up), or created or are
 * assigned to (at Mine and up) — for each backlog kind, at the level that kind is
 * set to. Off stays off; All already covers them. Default off: switching it on is
 * the user's call, nobody's feed grows on deploy. (f-01956)
 */
export const BACKLOG_CHILD_ITEMS_OPTION = 'backlog.childItems';
export const NOTIFICATION_OPTIONS = [
    {
        key: BACKLOG_CHILD_ITEMS_OPTION, category: 'backlog',
        label: 'Include child items of my Features',
        description: 'Also notify me about User Stories and Bugs directly under a Feature I follow, created or am assigned to, for each kind above at the level it is set to.',
        // f-02061 adds the description / acceptance-criteria kind: a child's content
        // change reaches the Feature's people the same way.
        kinds: ['backlog.status', 'backlog.assigned', 'backlog.commented', BACKLOG_CONTENT_KIND],
        default: false,
    },
];
const OPTION_BY_KEY = new Map(NOTIFICATION_OPTIONS.map((o) => [o.key, o]));
export function optionDef(key) {
    return OPTION_BY_KEY.get(key);
}
/**
 * Whether option `key` is on for a user whose stored options are `values`.
 * Conservative like resolveKindMode: an option nobody declared is off, and a
 * stored value that is not a boolean falls back to the option's default.
 */
export function resolveNotificationOption(key, values) {
    const def = OPTION_BY_KEY.get(key);
    if (!def)
        return false;
    const stored = values?.[key];
    return typeof stored === 'boolean' ? stored : def.default;
}
/** True when option `key` changes how `kind` is delivered. */
export function optionAppliesTo(key, kind) {
    return OPTION_BY_KEY.get(key)?.kinds.includes(kind) ?? false;
}
/** The options a user may see: those changing at least one kind they can see. */
export function visibleOptions(kinds) {
    const visible = new Set(kinds.map((k) => k.key));
    return NOTIFICATION_OPTIONS.filter((o) => o.kinds.some((k) => visible.has(k)));
}
/**
 * Apply a settings-screen patch to a user's stored options: only declared options
 * with boolean values are kept, everything already stored survives unless the
 * patch names it. Pure — the API validates with it, and so can a client. (f-01956)
 */
export function mergeNotificationOptions(current, patch) {
    const next = {};
    for (const src of [current, patch]) {
        if (!src || typeof src !== 'object')
            continue;
        for (const [key, value] of Object.entries(src)) {
            if (OPTION_BY_KEY.has(key) && typeof value === 'boolean')
                next[key] = value;
        }
    }
    return next;
}
/**
 * The extra line the settings row shows when the option cannot change anything
 * for the user's current levels — every kind it covers is Off (nothing to widen),
 * every one is All (already everything), or each is one of the two. Null while
 * any kind sits at Follow or Mine, where it does something. Pure, so the copy the
 * settings screen shows is tested. (f-01956)
 */
export function notificationOptionStateHint(option, modeOf) {
    const n = option.kinds.length;
    if (!n)
        return null;
    const modes = option.kinds.map(modeOf);
    const they = n === 1 ? 'it is' : n === 2 ? 'both are' : n === 3 ? 'all three are' : 'they are all';
    if (modes.every((m) => m === 'off'))
        return `No effect while ${they} Off.`;
    if (modes.every((m) => m === 'all'))
        return 'All already includes them.';
    if (modes.every((m) => m === 'off' || m === 'all'))
        return 'No effect at these levels: Off stays off, and All already includes them.';
    return null;
}
//# sourceMappingURL=notifications.js.map